/**
 * useRefreshPrevention Hook
 * Prevents page refresh/close when offline with pending transactions
 * 
 * Features:
 * - Blocks Ctrl+R, Ctrl+Shift+R, F5, browser refresh button
 * - Shows warning dialog before allowing navigation
 * - Only active when offline AND has pending transactions
 */

import { useEffect, useCallback, useState } from 'react';
import db from './db';
import Swal from 'sweetalert2';

/**
 * Hook to prevent page refresh when offline with pending data
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether prevention is enabled (default: true)
 * @param {boolean} options.showWarning - Whether to show SweetAlert warning (default: true)
 */
const useRefreshPrevention = (options = {}) => {
  const { enabled = true, showWarning = true } = options;
  
  const [isBlocking, setIsBlocking] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [offlineOrdersCount, setOfflineOrdersCount] = useState(0);

  // Check if we should block refresh
  const checkBlockingStatus = useCallback(async () => {
    if (!enabled) {
      setIsBlocking(false);
      return false;
    }

    const isOffline = !navigator.onLine;
    
    if (!isOffline) {
      setIsBlocking(false);
      return false;
    }

    try {
      // Check pending transactions
      const pending = await db.getPendingCount();
      setPendingCount(pending);
      
      // Check offline orders
      const orders = await db.getOfflineOrders();
      const offlineOrders = orders.filter(o => o.offlineOrder === true);
      setOfflineOrdersCount(offlineOrders.length);
      
      // Block if we have pending transactions OR offline orders
      const shouldBlock = pending > 0 || offlineOrders.length > 0;
      setIsBlocking(shouldBlock);
      
      return shouldBlock;
    } catch (error) {
      console.error('Error checking blocking status:', error);
      setIsBlocking(false);
      return false;
    }
  }, [enabled]);

  // Handle beforeunload event (browser close/refresh)
  const handleBeforeUnload = useCallback((event) => {
    if (!isBlocking) return;
    
    // Standard way to show browser's native "Leave site?" dialog
    event.preventDefault();
    event.returnValue = ''; // Required for Chrome
    
    return 'You have unsaved offline data. Are you sure you want to leave?';
  }, [isBlocking]);

  // Handle keyboard shortcuts (Ctrl+R, F5, Ctrl+Shift+R)
  const handleKeyDown = useCallback(async (event) => {
    if (!enabled) return;
    
    const isRefreshKey = (
      event.key === 'F5' ||
      (event.ctrlKey && event.key === 'r') ||
      (event.ctrlKey && event.key === 'R') ||
      (event.ctrlKey && event.shiftKey && event.key === 'r') ||
      (event.ctrlKey && event.shiftKey && event.key === 'R')
    );

    if (!isRefreshKey) return;

    // Check current status
    const shouldBlock = await checkBlockingStatus();
    
    if (shouldBlock) {
      event.preventDefault();
      event.stopPropagation();
      
      if (showWarning) {
        const result = await Swal.fire({
          icon: 'warning',
          title: '⚠️ Offline Data Warning',
          html: `
            <div style="text-align: left; padding: 10px;">
              <p style="margin-bottom: 15px; font-size: 16px;">
                <strong>You are currently OFFLINE</strong> with unsaved data:
              </p>
              <ul style="margin-left: 20px; margin-bottom: 15px;">
                ${pendingCount > 0 ? `<li><strong>${pendingCount}</strong> pending transaction(s)</li>` : ''}
                ${offlineOrdersCount > 0 ? `<li><strong>${offlineOrdersCount}</strong> offline order(s)</li>` : ''}
              </ul>
              <p style="color: #d32f2f; font-weight: bold;">
                ⛔ Refreshing will LOSE all offline data!
              </p>
              <p style="margin-top: 10px; color: #666;">
                Please wait for network connection to sync your data.
              </p>
            </div>
          `,
          showCancelButton: true,
          confirmButtonText: '🔄 Refresh Anyway (Lose Data)',
          cancelButtonText: '✓ Stay on Page',
          confirmButtonColor: '#d32f2f',
          cancelButtonColor: '#4caf50',
          reverseButtons: true,
          focusCancel: true,
          allowOutsideClick: false,
          allowEscapeKey: true
        });

        if (result.isConfirmed) {
          // User chose to refresh anyway - allow it
          console.log('⚠️ User chose to refresh despite offline data warning');
          window.location.reload();
        } else {
          console.log('✅ User chose to stay on page');
        }
      }
      
      return false;
    }
  }, [enabled, showWarning, checkBlockingStatus, pendingCount, offlineOrdersCount]);

  // Handle visibility change (tab switching)
  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === 'visible') {
      checkBlockingStatus();
    }
  }, [checkBlockingStatus]);

  // Handle online/offline status change
  const handleOnlineStatusChange = useCallback(() => {
    checkBlockingStatus();
  }, [checkBlockingStatus]);

  // Setup event listeners
  useEffect(() => {
    if (!enabled) return;

    // Initial check
    checkBlockingStatus();

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('keydown', handleKeyDown, true); // Use capture phase
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    // Periodic check every 5 seconds
    const intervalId = setInterval(checkBlockingStatus, 5000);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
      clearInterval(intervalId);
    };
  }, [
    enabled,
    handleBeforeUnload,
    handleKeyDown,
    handleVisibilityChange,
    handleOnlineStatusChange,
    checkBlockingStatus
  ]);

  return {
    isBlocking,
    pendingCount,
    offlineOrdersCount,
    checkBlockingStatus
  };
};

export default useRefreshPrevention;
